// Shared, accessible status indicator — the monitoring "hero" signal.
//
// Always renders icon + text label + color (never color alone) so it's
// colorblind-safe (WCAG 1.4.1), and keys off the semantic status tokens
// (bg-status-*-soft / text-status-*) rather than hardcoded Tailwind colors.
// Replaces the per-route StatusBadge copies.

import { Check, X, AlertTriangle, CircleDashed, Pause, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'

export type StatusKind = 'up' | 'down' | 'degraded' | 'unknown' | 'paused' | 'pending'

const MAP: Record<
  StatusKind,
  { label: string; Icon: typeof Check; badge: string; dot: string; title: string; pulse?: boolean }
> = {
  up: { label: 'Up', Icon: Check, badge: 'bg-status-up-soft text-status-up', dot: 'bg-status-up', title: 'Last check was successful' },
  down: { label: 'Down', Icon: X, badge: 'bg-status-down-soft text-status-down', dot: 'bg-status-down', title: 'Last check failed' },
  degraded: { label: 'Degraded', Icon: AlertTriangle, badge: 'bg-status-degraded-soft text-status-degraded', dot: 'bg-status-degraded', title: 'Degraded — a visual or partial change was detected' },
  unknown: { label: 'Unknown', Icon: CircleDashed, badge: 'bg-status-unknown-soft text-status-unknown', dot: 'bg-status-unknown', title: 'Last check could not determine up/down' },
  paused: { label: 'Paused', Icon: Pause, badge: 'bg-status-paused-soft text-status-paused', dot: 'bg-status-paused', title: 'Monitor is paused — no checks running' },
  pending: { label: 'Pending', Icon: Clock, badge: 'bg-status-unknown-soft text-status-unknown', dot: 'bg-status-unknown', title: 'First check runs within 1 minute', pulse: true },
}

/// Resolve the various status strings used across the app into one StatusKind.
/// A paused/disabled monitor reads "paused" regardless of its last check; a
/// monitor with no check yet reads "pending".
export function resolveStatusKind(status?: string, monitorStatus?: string): StatusKind {
  if (monitorStatus === 'paused' || monitorStatus === 'disabled') return 'paused'
  if (!status || status === 'pending') return 'pending'
  if (status === 'up' || status === 'operational') return 'up'
  if (status === 'down') return 'down'
  if (status === 'degraded' || status === 'visual_regression' || status === 'partial_outage') return 'degraded'
  return 'unknown'
}

export function StatusBadge({
  status,
  monitorStatus,
  kind,
  label,
  className,
}: {
  status?: string
  monitorStatus?: string
  kind?: StatusKind
  label?: string
  className?: string
}) {
  const k = kind ?? resolveStatusKind(status, monitorStatus)
  const m = MAP[k]
  const Icon = m.Icon
  return (
    <span
      role="status"
      title={m.title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium',
        m.badge,
        m.pulse && 'animate-pulse',
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {label ?? m.label}
    </span>
  )
}

/// Compact dot variant for dense lists. Still carries an accessible label via
/// aria-label + title (the row text supplies the visible label).
export function StatusDot({
  status,
  monitorStatus,
  kind,
  className,
}: {
  status?: string
  monitorStatus?: string
  kind?: StatusKind
  className?: string
}) {
  const k = kind ?? resolveStatusKind(status, monitorStatus)
  const m = MAP[k]
  return (
    <span
      role="status"
      aria-label={m.label}
      title={m.title}
      className={cn('inline-block h-2.5 w-2.5 rounded-full', m.dot, m.pulse && 'animate-pulse', className)}
    />
  )
}
